import React from 'react';

import Axios from 'axios';

import styled from 'styled-components';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { forceReloadFollowup, useDeleteFollowup } from '@hooks/API/Event/UseFollowup';

import HorizontalPeriod from '@components/Dates/HorizontalPeriod';
import AcknowledgementEvent from '@components/EventButtons/AcknowledgementEvent';
import CancelEvent from '@components/EventButtons/CancelEvent';
import { ACTIONS, useFollowUp } from '@components/EventDrawer/FollowUpEvent/FollowUpContext';
import { NwButton, NwButtonGroup } from '@components/Gui/NwButton';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwUpload from '@components/Gui/NwUpload';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1.5rem 0.75rem 0.5rem;
    /* border-bottom: ${(props) => props.theme.DefaultBorder}; */
    gap: 1rem;

    .left-content {
        text-align: left;
        flex: 1;
        padding-left: 1.5rem;
        h1 {
            margin: 0.75rem 0;
            font-size: 1.25rem;
        }
        .horizontal-period-container {
            width: 10rem;
            display: flex;
            justify-content: flex-start;
            align-items: center;
            gap: 1rem;
            .line-date {
                white-space: nowrap;
            }
            .line-time {
                white-space: nowrap;
            }
        }
    }
    .middle-content {
        text-align: left;
    }
    .right-content {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .nw-dropdown-menu-button-group {
            justify-content: center;
        }
    }
    &.is-cancelled {
        h1 {
            ${(props) => props.theme.RedLineThrough(1)}
        }
    }
`;

const ButtonGroupWrapper = styled.div`
    .ant-btn:nth-child(2),
    span:nth-child(2) > .ant-btn,
    a:nth-child(2) > .ant-btn {
        border-right: 1px solid #d9d9d9 !important;
    }
    .ant-btn:nth-child(2),
    span:nth-child(2) > .ant-btn,
    a:nth-child(2) > .ant-btn {
        border-top-right-radius: 6px;
        border-bottom-right-radius: 6px;
    }
`;

export const FollowUpGroupInstanceModelNameAndDate = ({ event, uploadEndpoint, onUploadEnd }) => {
    const { groupType, followUpData, dispatch, afterUpdate, afterRemove, followUpID } = useFollowUp();
    const { mutateAsync: onDeleteFollowup } = useDeleteFollowup();

    const handleRemoveEvent = async () => {
        try {
            await onDeleteFollowup({ ID: event.ID, reloadID: followUpID });
            afterUpdate();
        } catch (error) {
            console.error('Remove Event Date Model Error => ', error);
        }
    };

    const handleUnlinkEvent = async () => {
        try {
            await Axios.put(`/followups/UnLink/${event.ID}`);
            // handleUpdateFollowUps();
            afterUpdate();
        } catch (error) {
            console.error('Unlink Event Error => ', error);
        }
    };

    const handleAddComment = () => {
        dispatch({ type: ACTIONS.SET_ADD_COMMENT_FOR_EVENT, payload: event.ID });
    };

    const actions = {
        onEdit: () => {
            dispatch({ type: ACTIONS.SET_EDIT_GROUP_INSTANCE, payload: event });
            dispatch({ type: ACTIONS.SET_IS_EDIT, payload: true });
        },
        onDelete: async () => {
            await handleRemoveEvent(followUpData.ID);
            if (event.ID === followUpID) {
                afterRemove(true);
            } else {
                afterUpdate();
            }
        },
        onUnlink: handleUnlinkEvent,
        onAddComment: handleAddComment,
        onAcknowledgmentUpdate: () => {
            forceReloadFollowup(followUpData.ID);
            afterUpdate();
        },
        onCancelUpdate: () => {
            forceReloadFollowup(followUpData.ID);
            afterUpdate();
        },
    };

    const buttonactions = [
        { name: '', key: 'edit', pinned: true, icon: byPrefixAndName.fasl['pen'], action: actions.onEdit },
        {
            name: 'delete',
            type: 'delete',
            key: 'delete',
            icon: byPrefixAndName.fasl['trash-alt'],
            confirmationPlacement: 'left',
            action: actions.onDelete,
        },
    ];

    buttonactions.push({
        name: 'add comment',
        key: 'add-comment',
        icon: byPrefixAndName.fass['comment-plus'],
        action: actions.onAddComment,
    });

    if (actions.onUnlink && groupType === 'models') {
        buttonactions.push({
            name: 'unlink',
            key: 'unlink',
            icon: byPrefixAndName.fasl['unlink'],
            action: actions.onUnlink,
        });
    }

    return (
        <Wrapper className={event.Cancel ? 'is-cancelled' : null}>
            <div className="left-content">
                {groupType === 'period' ? (
                    <HorizontalPeriod startDate={event.StartDate} size="big" time={true} endDate={event.EndDate} />
                ) : (
                    <h1>{getContactName(event.ModelID)}</h1>
                )}
            </div>
            <div className="middle-content">
                {(actions.onAcknowledgmentUpdate || actions.onCancelUpdate) && (
                    <ButtonGroupWrapper>
                        <NwButtonGroup right>
                            <AcknowledgementEvent
                                button
                                onStatusChanged={actions.onAcknowledgmentUpdate}
                                acknowledgmentValue={event.Acknowledgment}
                                type="FollowUp"
                                eventId={event.ID}
                            />
                            <CancelEvent
                                button
                                onStatusChanged={actions.onCancelUpdate}
                                cancelValue={event.Cancel}
                                type="FollowUp"
                                eventId={event.ID}
                            />
                        </NwButtonGroup>
                    </ButtonGroupWrapper>
                )}
            </div>
            <div className="right-content">
                <NwDropDownMenu actions={buttonactions} />
                <NwUpload
                    maxSize={20}
                    showList={false}
                    types={['all']}
                    onUploadEnd={onUploadEnd}
                    endpoint={uploadEndpoint}
                >
                    <NwButton
                        ghost
                        icon={byPrefixAndName.fass['cloud-arrow-up']}
                        tooltip={`Add document for ${getContactName(event.ModelID)}`}
                    />
                </NwUpload>
            </div>
        </Wrapper>
    );
};
